import React, { useState, useEffect } from "react";
import "../styles/verify.css";
import idIcon from "../assets/id_icon.png";
import personIcon from "../assets/person_icon.png";
import { useNavigate } from "react-router-dom";

// ✅ Upload image to Cloudinary
const uploadToCloudinary = async (file) => {
  const url = `https://api.cloudinary.com/v1_1/dlmrcsaqf/image/upload`;
  const formData = new FormData();
  formData.append("file", file);
  formData.append("upload_preset", "unsigned_preset");
  formData.append("folder", "firetrace_uploads");

  try {
    const res = await fetch(url, { method: "POST", body: formData });
    const data = await res.json();
    return data.secure_url || null;
  } catch (err) {
    console.error("Cloudinary upload error:", err);
    return null;
  }
};

const VerifyWithSelfie = () => {
  const [idImage, setIdImage] = useState(null);
  const [selfieImage, setSelfieImage] = useState(null);
  const [previewID, setPreviewID] = useState(null);
  const [previewSelfie, setPreviewSelfie] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [showPendingPopup, setShowPendingPopup] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    contact: "",
    address: "",
  });
  const [modal, setModal] = useState({ show: false, message: "" });

  const navigate = useNavigate();

  // ✅ Load user info
  useEffect(() => {
    const storedUser = JSON.parse(localStorage.getItem("user"));
    if (storedUser) {
      setFormData((prev) => ({
        ...prev,
        name: storedUser.name || "",
        email: storedUser.email || "",
        contact: storedUser.phone || "",
        address: storedUser.address || "",
      }));

      if (storedUser.isVerified === "pending") {
        setShowPendingPopup(true);
      }
    }
  }, []);

  const handleFileChange = (e, type) => {
    const file = e.target.files[0];
    if (!file) return;

    const validTypes = ["image/jpeg", "image/png", "image/jpg"];
    if (!validTypes.includes(file.type)) {
      return setModal({ show: true, message: "Please upload a valid JPG or PNG file." });
    }

    if (type === "id") {
      setIdImage(file);
      setPreviewID(URL.createObjectURL(file));
    } else {
      setSelfieImage(file);
      setPreviewSelfie(URL.createObjectURL(file));
    }
  };

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  // ✅ Submit verification
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!idImage || !selfieImage) {
      return setModal({ show: true, message: "Please upload both ID and selfie images." });
    }

    try {
      const storedUser = JSON.parse(localStorage.getItem("user"));
      if (!storedUser) {
        return setModal({ show: true, message: "User not found. Please log in again." });
      }

      setIsUploading(true);

      const [idUrl, selfieUrl] = await Promise.all([
        uploadToCloudinary(idImage),
        uploadToCloudinary(selfieImage),
      ]);

      if (!idUrl || !selfieUrl) {
        setIsUploading(false);
        return setModal({ show: true, message: "Failed to upload one or more images. Try again." });
      }

      const response = await fetch(
        `https://firetrace-capstone.onrender.com/api/users/${storedUser._id}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            name: formData.name,
            email: formData.email,
            phone: formData.contact,
            address: formData.address,
            ID_Picture: idUrl,
            selfie: selfieUrl,
          }),
        }
      );

      const data = await response.json();
      setIsUploading(false);

      if (response.ok) {
        setModal({ show: true, message: "Verification submitted successfully! Please wait for admin approval." });
        localStorage.setItem("user", JSON.stringify(data.user));
        window.location.reload();
      } else {
        setModal({ show: true, message: "" + (data.message || "Upload failed.") });
      }
    } catch (error) {
      setIsUploading(false);
      console.error(error);
      setModal({ show: true, message: "Something went wrong. Try again later." });
    }
  };

  return (
    <div className="verify-page">
      <h1 className="verify-title">Identity Verification</h1>
      <p className="verify-subtitle">
        Please upload your government ID and a selfie for verification.
      </p>

      <div className="verify-grid">
        {/* Government ID */}
        <div className="verify-box">
          <h2>Government ID</h2>
          <div className="upload-area">
            {previewID ? (
              <label className="preview-container">
                <img src={previewID} alt="ID Preview" className="preview-image" />
                <div className="overlay-text">Click to change image</div>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => handleFileChange(e, "id")}
                />
              </label>
            ) : (
              <label className="upload-label">
                <img src={idIcon} alt="ID icon" className="upload-icon" />
                <span>Upload or Drag and Drop</span>
                <small>Use valid ID photo only</small>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => handleFileChange(e, "id")}
                />
              </label>
            )}
          </div>
        </div>

        {/* Selfie */}
        <div className="verify-box">
          <h2>Take a Selfie</h2>
          <div className="upload-area">
            {previewSelfie ? (
              <label className="preview-container">
                <img
                  src={previewSelfie}
                  alt="Selfie Preview"
                  className="preview-image"
                />
                <div className="overlay-text">Click to change image</div>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => handleFileChange(e, "selfie")}
                />
              </label>
            ) : (
              <label className="upload-label">
                <img src={personIcon} alt="Person icon" className="upload-icon" />
                <span>Click or Drag to Upload</span>
                <small>Upload a clear selfie</small>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => handleFileChange(e, "selfie")}
                />
              </label>
            )}
          </div>
        </div>
      </div>

      {/* Personal Info */}
      <form className="verify-form" onSubmit={handleSubmit}>
        <h3>Personal Information</h3>
        <div className="form-grid">
          <input
  type="text"
  name="name"
  placeholder="Full Name"
  value={formData.name}
  onChange={(e) => {
    const value = e.target.value;
    const lettersOnly = /^[A-Za-z\s]*$/; // ✅ allows letters and spaces only
    if (lettersOnly.test(value)) {
      setFormData({ ...formData, name: value });
    }
  }}
  required
/>

          <input
            type="email"
            name="email"
            placeholder="Email Address"
            value={formData.email}
            onChange={handleInputChange}
            required
          />
   <input
  type="text"
  name="contact"
  placeholder="Contact Number"
  value={formData.contact}
  onChange={(e) => {
    // Allow only numeric input
    const value = e.target.value.replace(/[^0-9]/g, "");
    handleInputChange({ target: { name: "contact", value } });
  }}
  required
  pattern="09[0-9]{9}"    // Must start with 09 and have 11 digits total
  minLength={11}
  maxLength={11}
  title="Contact number must be 11 digits and start with 09"
/>


          <input
            type="text"
            name="address"
            placeholder="Home Address"
            value={formData.address}
            onChange={handleInputChange}
            required
          />
        </div>

        <button type="submit" className="verify-submit" disabled={isUploading}>
          {isUploading ? "⏳ Uploading..." : "Submit for Verification"}
        </button>
      </form>
              {/* ✅ Modal */}
      {modal.show && (
        <div className="modal-overlay01" onClick={() => setModal({ show: false, message: "" })}>
          <div className="modal-content01" onClick={(e) => e.stopPropagation()}>
            <p>{modal.message}</p>
            <button onClick={() => setModal({ show: false, message: "" })}>Close</button>
          </div>
        </div>
      )}
      {/* ✅ Pending Verification Popup */}
      {showPendingPopup && (
        <div className="pending-popup-overlay">
          <div className="pending-popup-box">
            <h2>Hang Tight!</h2>
            <p>
              The admin is verifying your account. You’ll receive an email once it’s done.
            </p>
           <button
  className="pending-popup-btn"
  onClick={() => {
    setShowPendingPopup(false);
    navigate("/dashboard"); // ✅ React Router redirect
  }}
>
  OK
</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default VerifyWithSelfie;
